/**
 * User Query Hook
 * 
 * React Query hook for fetching a user by ID for account setup.
 * 
 * ## Features
 * - Type-safe query with error handling
 * - Automatic caching and refetching
 * - Loading and error states
 * - Used by Account Setup page to fetch user details
 * 
 * @module user/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useTenantHandle } from '~/services/appRuntime';
import { userService } from '../api/userService';
import type { UserWithClubs } from '../types';

export interface UseUserQueryOptions {
  /** User ID to fetch */
  userId: string | number | null | undefined;

  /** Whether the query is enabled (default: true if userId exists) */
  enabled?: boolean;
}

/**
 * Fetch a user by ID for account setup
 * 
 * @example
 * ```tsx
 * const { data: user, isLoading, error } = useUserQuery({
 *   userId: '123',
 * });
 * 
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * 
 * return (
 *   <div>
 *     <h1>Welcome, {user?.email}</h1>
 *     {user?.clubs?.map(club => (
 *       <div key={club.id}>{club.name}</div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useUserQuery({
  userId,
  enabled = true,
}: UseUserQueryOptions) {
  const [tenantHandle] = useTenantHandle();
  
  return useQuery<UserWithClubs, Error>({
    queryKey: userId && tenantHandle
      ? queryKeys.tenant.userDetail(tenantHandle, userId)
      : queryKeys.disabled.user(),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }

      return userService.getUserForAccountSetup(userId);
    },
    enabled: enabled && !!userId && !!tenantHandle,
    ...cachePresets.stable,
  });
}
